import React, { useState } from 'react';
import {
  Box,
  Text,
  Input,
  VStack,
  Flex,
} from '@chakra-ui/react';
import { useDropzone, Accept } from 'react-dropzone';
import ApprovedContentPreview from './ApprovedContentPreview';
import { BottomSheetContent } from './BottomSheet';
import BrandFeedbackBody from './BrandFeedbackBody';
import { SheetButton } from './counter-flow/sheetParts';
import {
  SubmissionSheetTitle,
  FeedbackBox,
  DropzoneFace,
  dropzoneBoxProps,
  acceptedFormatsLabel,
  UploadProgressRow,
  linkInputProps,
} from './submissionSheetParts';
import { ff } from '../../../theme';

const STORY_ACCEPT: Accept = {
  'video/*': ['.mp4', '.mov'],
  'image/*': ['.jpeg', '.jpg', '.png', '.heic', '.heif'],
};

interface UploadFileOrSubmitLinkModalProps {
  modalHeader: string;
  onClose: () => void;
  onSubmitLink: (link: string) => void;
  onSubmitFile: (file: File) => void;
  onStoryExpiredClick: () => void;
  showFileUpload: boolean;
  showFeedback: boolean;
  rejectionReason: string | null;
  isSubmitting: boolean;
  uploadProgress: number;
  /** Approved draft to post, when the campaign ran a draft-approval round. */
  approvedContentUrl?: string;
}

const UploadFileOrSubmitLinkModal: React.FC<UploadFileOrSubmitLinkModalProps> = ({
  modalHeader,
  onClose,
  onSubmitLink,
  onSubmitFile,
  onStoryExpiredClick,
  showFileUpload,
  showFeedback,
  rejectionReason,
  isSubmitting,
  uploadProgress,
  approvedContentUrl,
}) => {
  const [postLink, setPostLink] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [activeMethod, setActiveMethod] = useState<'link' | 'file' | null>(null);

  const dropZoneBlockedByLink = activeMethod === 'link';
  const fileDropDisabled = dropZoneBlockedByLink || !showFileUpload;
  const linkInputDisabled = activeMethod === 'file';

  const onDrop = (acceptedFiles: File[]) => {
    setUploadedFile(acceptedFiles[0]);
    setActiveMethod('file');
    setPostLink('');
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    disabled: fileDropDisabled,
    accept: STORY_ACCEPT,
    maxFiles: 1,
    multiple: false,
    useFsAccessApi: false,
    noClick: !showFileUpload,
  });

  const handleUploadZoneClick = () => {
    if (!showFileUpload) {
      onStoryExpiredClick();
    } else {
      open();
    }
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPostLink(value);
    if (value.trim()) {
      setActiveMethod('link');
      setUploadedFile(null);
    } else {
      setActiveMethod(null);
    }
  };

  const handleMarkAsPosted = () => {
    if (activeMethod === 'link') {
      onSubmitLink(postLink.trim());
    } else if (activeMethod === 'file' && uploadedFile) {
      onSubmitFile(uploadedFile);
    }
  };

  const canSubmit = activeMethod !== null && !isSubmitting;

  return (
    <BottomSheetContent onClose={onClose}>
      {/* Scrollable Content */}
      <Box overflowY="auto" px="20px" pt={1} pb="28px" fontFamily="display" color={ff.ink}>
        {/* Title + Subtitle */}
        <SubmissionSheetTitle title={modalHeader} context="Post to Instagram Story" />

        {/* Feedback */}
        {showFeedback && rejectionReason && (
          <Box mb="16px">
            <FeedbackBox lead="Edit requested:">
              <BrandFeedbackBody feedback={rejectionReason} />
            </FeedbackBox>
          </Box>
        )}

        <VStack spacing="16px" align="stretch">
          {/* Approved draft */}
          {approvedContentUrl && (
            <ApprovedContentPreview
              url={approvedContentUrl}
              label="Approved draft"
              fileNameBase={modalHeader}
            />
          )}

          {/* Instructions */}
          <Box as="section" aria-label="Time to post" p="16px" borderRadius="18px" bg={ff.grey}>
            <Text as="h3" m={0} mb="12px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2}>
              Time to post
            </Text>
            <VStack as="ol" m={0} p={0} listStyleType="none" align="stretch" spacing="10px">
              {['Post to your Instagram Story', 'Upload the file or paste your live story link below to verify'].map((step, i) => (
                <Flex as="li" key={i} gap="10px" fontSize="14px" lineHeight={1.4} color="#2A2A2C">
                  <Flex flex="none" w="24px" h="24px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center" fontSize="12px" fontWeight={600}>
                    {i + 1}
                  </Flex>
                  <Text as="span">{step}</Text>
                </Flex>
              ))}
            </VStack>
          </Box>

          {/* Upload File */}
          <Box
            {...getRootProps()}
            onClick={handleUploadZoneClick}
            {...dropzoneBoxProps}
            cursor={dropZoneBlockedByLink ? 'not-allowed' : 'pointer'}
            opacity={dropZoneBlockedByLink ? 0.4 : 1}
            pointerEvents={dropZoneBlockedByLink ? 'none' : 'auto'}
          >
            <input {...getInputProps()} />
            <DropzoneFace
              label={uploadedFile ? uploadedFile.name : 'Upload file'}
              formats={uploadedFile ? undefined : acceptedFormatsLabel(STORY_ACCEPT)}
            />
          </Box>

          {/* POST LINK */}
          <Box opacity={linkInputDisabled ? 0.4 : 1}>
            <Text as="label" htmlFor="ff-story-link" display="block" fontSize="14px" fontWeight={600} mb="8px">
              Instagram story link
            </Text>
            <Input
              id="ff-story-link"
              value={postLink}
              onChange={handleLinkChange}
              isDisabled={linkInputDisabled}
              placeholder="https://instagram.com/story/..."
              {...linkInputProps(false)}
              _disabled={{ opacity: 1, cursor: 'not-allowed' }}
            />
          </Box>

          {isSubmitting && uploadedFile && (
            <UploadProgressRow fileName={uploadedFile.name} percent={uploadProgress} />
          )}

          {/* Mark as posted */}
          <SheetButton
            tone="lime"
            onClick={handleMarkAsPosted}
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            w="full"
            fontSize="16px"
          >
            Mark as posted
          </SheetButton>
        </VStack>
      </Box>
    </BottomSheetContent>
  );
};

export default UploadFileOrSubmitLinkModal;
